import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api.service';
import { ApiEndpoint } from '../types';
import TableViewer from './TableViewer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faFolder, 
  faFolderOpen, 
  faPlay, 
  faRedo, 
  faHourglassHalf, 
  faTimes, 
  faExclamationTriangle, 
  faCheckCircle, 
  faChartBar, 
  faRocket, 
  faClipboardList, 
  faDownload,
  faCopy,
  faSave,
  faCog,
  faEdit,
  faUsers,
  faLink,
  faTrash,
  faLightbulb
} from '@fortawesome/free-solid-svg-icons';

const ApiExplorer: React.FC = () => {
  const [endpoints, setEndpoints] = useState<{ [key: string]: ApiEndpoint[] }>({});
  const [selectedEndpoint, setSelectedEndpoint] = useState<ApiEndpoint | null>(null);

  const [dynamicParams, setDynamicParams] = useState<{ [key: string]: string | boolean }>({});
  const [response, setResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeCategory, setActiveCategory] = useState<string>('');
  
  // Search functionality
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredEndpoints, setFilteredEndpoints] = useState<{ [key: string]: ApiEndpoint[] }>({});

  const [userTypes, setUserTypes] = useState<string[]>([]);
  
  // State to persist loaded data for each endpoint
  const [endpointDataCache, setEndpointDataCache] = useState<{
    [endpointName: string]: {
      data: any;
      params: { [key: string]: string | boolean };
      timestamp: string;
      error?: string;
    }
  }>({});
  
  // Add request cancellation support
  const [currentRequestController, setCurrentRequestController] = useState<AbortController | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<string>('');
  
  // State for multiple deposit transactions
  const [depositTransactions, setDepositTransactions] = useState<Array<{
    id: string;
    userId: string;
    transactionType: string;
    value: string;
    comment: string;
  }>>([]);

  // Common parameter configurations for different endpoints
  const getParameterConfig = (endpointPath: string) => {

    const configs: { [key: string]: Array<{ name: string; label: string; type: string; placeholder?: string; required?: boolean; options?: Array<{value: string, label: string}> }> } = {
      'Users Types': [
        { 
          name: 'name', 
          label: 'User Type', 
          type: 'select', 
          required: true,
          options: userTypes.length > 0 ? userTypes.map(userType => ({
            value: userType,
            label: userType
          })) : [
            { value: 'loading', label: 'Loading user types...' }
          ]
        },
        // { name: 'startDate', label: 'Start Date', type: 'date', required: false },
        // { name: 'endDate', label: 'End Date', type: 'date', required: false },
        // { name: 'limit', label: 'Limit', type: 'number', placeholder: '100', required: false },
        // { name: 'page', label: 'Page', type: 'number', placeholder: '1', required: false }
      ],
      'Users Points': [
        // No parameters - loads all data automatically
      ],
      'Users': [
        // No parameters - loads all users data automatically
      ],
      'Referral Users': [
        { name: 'month', label: 'Month', type: 'number', required: false },
        { name: 'year', label: 'Year', type: 'number', required: false },
        { name: 'dateType', label: 'Date Type', type: 'select', options: [{value: 'AchievedDate', label: 'Achieved Date'}, {value: 'referDate', label: 'Refer Date'}], required: false },
      ],
      'Referred Users': [
        { name: 'month', label: 'Month', type: 'number', required: false },
        { name: 'year', label: 'Year', type: 'number', required: false },
        { name: 'dateType', label: 'Date Type', type: 'select', options: [{value: 'AchievedDate', label: 'Achieved Date'}, {value: 'referDate', label: 'Refer Date'}], required: false },
      ],
      'User Details Per Product Per Action': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2025-05-20' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-05-20' },
        // { name: 'page', label: 'Page', type: 'number', placeholder: '1', required: false },
        // { name: 'limit', label: 'Limit', type: 'number', placeholder: '1000', required: false },
        // { name: 'allowPagination', label: 'Allow Pagination', type: 'boolean', required: false }
      ],
      
      'Warehouse Scan In/Out': [
                // No parameters - loads all data automatically
      ],
      'Scans per product per user type': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2024-01-01' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-05-20' },
      ],
      'Scan Per Product details': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2023-01-01' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-12-31' },
      ],
      'Wallet Transactions': [
        { name: 'userId', label: 'User ID', type: 'text', placeholder: 'Enter user ID', required: false },
        { name: 'month', label: 'Month', type: 'number', required: false },
        { name: 'year', label: 'Year', type: 'number', required: false },
        { 
          name: 'transactionType', 
          label: 'Transaction Type', 
          type: 'select', 
          required: false,
          options: [
            { value: 'All', label: 'All' },
            { value: 'GIFTS', label: 'GIFTS' },
            { value: 'REFERING', label: 'REFERING' },
            { value: 'REWARD', label: 'REWARD' },
            { value: 'ENDOFMONTH', label: 'ENDOFMONTH' },
            { value: 'SCANOUT', label: 'SCANOUT' },
            { value: 'SCANIN', label: 'SCANIN' },
            { value: 'CASHINCENTIVE', label: 'CASHINCENTIVE' },
            { value: 'EXTRAINCENTIVE', label: 'EXTRAINCENTIVE' },
            { value: 'RECIEVETRANSFEREDINCENTIVE', label: 'RECIEVETRANSFEREDINCENTIVE' },
            { value: 'SCANUSE', label: 'SCANUSE' }
          ]
        },
      ],
      // Additional QR Codes endpoints
      'Deactivated Incentive QR': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2025-05-20' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-05-20' },
      ],
      'Configuration Result Per Product': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2025-05-20' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-05-20' },
      ],
      'Order Scanout From Warehouse': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2025-05-20' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-05-20' },
      ],
      'Warehouse Quantity Aggregation': [
        // No parameters - loads all data automatically
      ],
      // Wallet endpoints
      'Deliver Cash': [
        { name: 'month', label: 'Month', type: 'number', required: false },
        { name: 'year', label: 'Year', type: 'number', required: false },
        // No parameters - take supplier name
      ],
      'Deliver Transfer': [
        // No parameters - loads all data automatically
      ],
      'Welcome Bonus': [
        // { name: 'supplierId', label: 'Supplier ID', type: 'text', placeholder: 'Enter supplier ID', required: false },
        { name: 'day', label: 'Day', type: 'number', placeholder: 'Enter day (1-31)', required: false },
        { name: 'month', label: 'Month', type: 'number', placeholder: 'Enter month (1-12)', required: false },
        { name: 'year', label: 'Year', type: 'number', placeholder: 'Enter year (e.g., 2024)', required: false }
      ],
      // Payment/Paymob endpoints
      'Paymob': [
        { name: 'month', label: 'Month', type: 'number', placeholder: 'Enter month (1-12)', required: false },
        { name: 'year', label: 'Year', type: 'number', placeholder: 'Enter year (e.g., 2024)', required: false }
      ],
      // Invoice endpoints
      'invoice-photos': [
        // No parameters - loads all data automatically
      ],
      // Transaction Logs endpoints
      'reset-logs': [
        { name: 'day', label: 'Day', type: 'number', placeholder: 'Enter day (1-31)', required: false },
        { name: 'month', label: 'Month', type: 'number', placeholder: 'Enter month (1-12)', required: false },
        { name: 'year', label: 'Year', type: 'number', placeholder: 'Enter year (e.g., 2024)', required: false }
      ],
      // Joining Requests endpoints
      'joining-requests': [
        // No parameters - loads all data automatically
      ],
      // Return System endpoints
      'Return System': [
        { 
          name: 'dateType', 
          label: 'Date Type', 
          type: 'select', 
          required: false,
          options: [
            { value: 'Return Date', label: 'Return Date' },
            { value: 'Transaction Date', label: 'Transaction Date' }
          ]
        },
        { name: 'day', label: 'Day', type: 'number', placeholder: 'Enter day (1-31)', required: false },
        { name: 'month', label: 'Month', type: 'number', placeholder: 'Enter month (1-12)', required: false },
        { name: 'year', label: 'Year', type: 'number', placeholder: 'Enter year (e.g., 2024)', required: false }
      ],
      'Return System - Returns': [
        { 
          name: 'dateType', 
          label: 'Date Type', 
          type: 'select', 
          required: false,
          options: [
            { value: 'Return Date', label: 'Return Date' },
            { value: 'Transaction Date', label: 'Transaction Date' }
          ]
        },
        { name: 'day', label: 'Day', type: 'number', placeholder: 'Enter day (1-31)', required: false },
        { name: 'month', label: 'Month', type: 'number', placeholder: 'Enter month (1-12)', required: false },
        { name: 'year', label: 'Year', type: 'number', placeholder: 'Enter year (e.g., 2024)', required: false }
      ],
      'Incentive': [
        { 
          name: 'dateType', 
          label: 'Date Type', 
          type: 'select', 
          required: false,
          options: [
            { value: 'Return Date', label: 'Return Date' },
            { value: 'Transaction Date', label: 'Transaction Date' }
          ]
        },
        { name: 'day', label: 'Day', type: 'number', placeholder: 'Enter day (1-31)', required: false },
        { name: 'month', label: 'Month', type: 'number', placeholder: 'Enter month (1-12)', required: false },
        { name: 'year', label: 'Year', type: 'number', placeholder: 'Enter year (e.g., 2024)', required: false }
      ],
      // Deposit Amount endpoints
      'Deposit Amount': [
        // No individual parameters - uses transaction table
      ],
      'Deposit Amount Log': [
        // { name: 'month', label: 'Month', type: 'number', required: false },
        // { name: 'year', label: 'Year', type: 'number', required: false },
        // { name: 'userId', label: 'User ID', type: 'text', placeholder: 'Enter user ID', required: false }
      ],
      'Gifts': [
        { name: 'month', label: 'Month', type: 'number', placeholder: 'Enter month (1-12)', required: false },
        { name: 'year', label: 'Year', type: 'number', placeholder: 'Enter year (e.g., 2024)', required: false }
      ],
    };
    
    return configs[endpointPath] || [];
    // Default fallback removed to prevent errors with undefined endpoints
  };

  const updateDynamicParams = (name: string, value: string | boolean) => {
    setDynamicParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Functions for managing deposit transactions
  const addDepositTransaction = () => {
    const newTransaction = {
      id: `tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId: '',
      transactionType: '',
      value: '',
      comment: ''
    };
    setDepositTransactions(prev => {
      const newTransactions = [...prev, newTransaction];
      return newTransactions;
    });
  };

  const updateDepositTransaction = (id: string, field: string, value: string) => {
    setDepositTransactions(prev => {
      const updated = prev.map(tx => tx.id === id ? { ...tx, [field]: value } : tx);
      return updated;
    });
  };

  const removeDepositTransaction = (id: string) => {
    setDepositTransactions(prev => prev.filter(tx => tx.id !== id));
  };

  const clearAllDepositTransactions = () => {
    setDepositTransactions([]);
  };

  const convertDynamicParamsToJson = () => {
    // Handle special case for allowPagination - default to true if not set
    const paramsWithDefaults = { ...dynamicParams };
    // if (selectedEndpoint?.endpoint === 'qr-codes/user-scan-analysis') {
    //   if (paramsWithDefaults['allowPagination'] === undefined) {
    //     paramsWithDefaults['allowPagination'] = true;
    //   }
    // }
    
    const filtered = Object.entries(paramsWithDefaults)
      .filter(([key, value]) => {
        if (typeof value === 'boolean') return true;
        if (typeof value === 'string') return value.trim() !== '';
        return false;
      })
      .reduce((acc, [key, value]) => {
        // Handle boolean values
        if (typeof value === 'boolean') {
          acc[key] = value;
        } else if (value === 'true') {
          acc[key] = true;
        } else if (value === 'false') {
          acc[key] = false;
        } else if (typeof value === 'string' && /^\d+$/.test(value)) {
          // Try to parse as number if it looks like one
          acc[key] = parseInt(value);
        } else if (typeof value === 'string' && /^\d+\.\d+$/.test(value)) {
          acc[key] = parseFloat(value);
        } else if (typeof value === 'string' && (key.toLowerCase().includes('date') || key === 'from' || key === 'to')) {
          // Handle date formatting - ensure consistent YYYY-MM-DD format
          let dateValue = value;
          
          // Convert from YYYY/MM/DD to YYYY-MM-DD format
          if (dateValue.includes('/')) {
            const parts = dateValue.split('/');
            if (parts.length === 3) {
              dateValue = `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
            }
          }
          
          // Validate and format the date
          const dateObj = new Date(dateValue);
          if (!isNaN(dateObj.getTime())) {
            // Format as YYYY-MM-DD
            const year = dateObj.getFullYear();
            const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
            const day = dateObj.getDate().toString().padStart(2, '0');
            acc[key] = `${year}-${month}-${day}`;
          } else {
            acc[key] = value; // Keep original if parsing fails
          }
        } else {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
    
    return JSON.stringify(filtered, null, 2);
  };

  // Handle endpoint selection and preserve/restore data
  const handleEndpointSelection = (endpoint: ApiEndpoint) => {
    // Cancel any existing request when switching endpoints
    if (currentRequestController) {
      currentRequestController.abort();
      setCurrentRequestController(null);
    }
    
    // Save current endpoint data to cache if we have data loaded
    if (selectedEndpoint && (response || error)) {
      setEndpointDataCache(prev => ({
        ...prev,
        [selectedEndpoint.endpoint]: {
          data: response,
          params: { ...dynamicParams },
          timestamp: new Date().toISOString(),
          error: error || undefined
        }
      }));
    }
    
    setSelectedEndpoint(endpoint);
    
    // Check if we have cached data for this endpoint
    const cachedData = endpointDataCache[endpoint.endpoint];
    if (cachedData) {
      // Restore cached data
      setResponse(cachedData.data);
      setDynamicParams(cachedData.params);
      setError(cachedData.error || '');
    } else {
      // Clear data for new endpoint
      setResponse(null);
      setError('');
      setDynamicParams({});
      setDepositTransactions([]); // Clear deposit transactions when switching endpoints
    }
    
    setLoading(false);
  };

  // Clear dynamic parameters when endpoint changes
  useEffect(() => {
    if (selectedEndpoint) {
      setDynamicParams({});
    }
  }, [selectedEndpoint]);

  // Format Users Points data similar to Google Apps Script
  const formatUsersPointsData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatUsersPointsData: Expected array, got:', typeof data);
      return [];
    }
    
    return data.map((user, index) => {
      try {
        return {
          'User ID': user?.userId || '',
          'Energya ID': user?.firebaseUserId || '',
          'First Name': user?.firstName || '',
          'Last Name': user?.lastName || '',
          'Phone': user?.phone || '',
          'Shop Name': user?.shopName || '',
          'Governorate': user?.governorate?.name || '',
          'District': user?.district?.name || '',
          'User Type': user?.userType?.name || '',
          'Segment': user?.segment?.name || '',
          'Total Points': user?.totalpoints || 0,
          'Total Money': user?.totalmoney || 0,
          'Total Wheels Points': user?.totalWheelspoints || 0,
          'Date': user?.createdAt ? new Date(user.createdAt).toLocaleString() : '',
          'Parent ID': user?.parentProfile?.userId || '',
          'Latitude': user?.coordinates?.[0] || '',
          'Longitude': user?.coordinates?.[1] || '',
          'First Scan Date': user?.firstScanDate ? new Date(user.firstScanDate).toLocaleString() : ''
        };
      } catch (error) {
        console.error(`Error formatting user data at index ${index}:`, error, user);
        return {
          'User ID': `Error_${index}`,
          'Error': 'Failed to format user data'
        };
      }
    });
  }, []);

  // Format Referral Users data similar to Google Apps Script
  const formatReferralUsersData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatReferralUsersData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define the exact column order as it should appear in the table
    const columnOrder = [
      'Transaction Reference',
      'User Type', 
      'User ID',
      'First Name',
      'Last Name', 
      'Phone',
      'Governorate',
      'District',
      'Referred Date',
      'Referred Time',
      'Referred User Type',
      'Referred ID', 
      'Referred Name',
      'Referred Phone',
      'Referred Governorate',
      'Referred District',
      'Referral Mechanism',
      'Referral Incentive',
      'Achieved Date'
    ];
    
    return data.map((referral, index) => {
      try {
        // Create object in the exact order defined above
        const orderedObject: any = {};
        
        // Map data to columns in exact order
        const dataMapping: { [key: string]: any } = {
          'Transaction Reference': referral?._id || '-',
          'User Type': referral?.userType || '-',
          'User ID': referral?.userId || '-',
          'First Name': referral?.firstName || '-',
          'Last Name': referral?.lastName || '-',
          'Phone': referral?.phone || '-',
          'Governorate': referral?.governorate || '-',
          'District': referral?.district || '-',
          'Referred Date': referral?.date || '-',
          'Referred Time': referral?.time || '-',
          'Referred User Type': referral?.Referred?.userType || '-',
          'Referred ID': referral?.Referred?.userId || '-',
          'Referred Name': referral?.Referred?.userName || '-',
          'Referred Phone': referral?.Referred?.phone || '-',
          'Referred Governorate': referral?.Referred?.governorate || '-',
          'Referred District': referral?.Referred?.district || '-',
          'Referral Mechanism': referral?.referalModelType?.name || '-',
          'Referral Incentive': referral?.wallettransaction?.value || '-',
          'Achieved Date': referral?.wallettransaction?.updatedAt || '-'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting referral data at index ${index}:`, error, referral);
        return {
          'Transaction Reference': `Error_${index}`,
          'Error': 'Failed to format referral data'
        };
      }
    });
  }, []);

  // Format Referred Users data based on Apps Script function structure
  const formatReferredUsersData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatReferredUsersData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script referredUsers function
    const columnOrder = [
      'Transaction ID',
      'Referring ID',
      'Referring Name',
      'Referred ID',
      'Referred Name',
      'Referral Model Type',
      'Incentive Status',
      'Min Consumption',
      'Referral Reward Value',
      'Percentage Per Scan',
      'Referral Period',
      'Total Points',
      'Value',
      'Total Money',
      'Refer Date'
    ];
    
    return data.map((referredUser, index) => {
      try {
        // Create object in the exact order defined above
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Transaction ID': referredUser?._id || '-',
          'Referring ID': referredUser?.referringId || '-',
          'Referring Name': referredUser?.referringName || '-',
          'Referred ID': referredUser?.referredId || '-',
          'Referred Name': referredUser?.referredName || '-',
          'Referral Model Type': referredUser?.referalModelTypeName || '-',
          'Incentive Status': referredUser?.isTakeIncentive ? 'pending' : 'given',
          'Min Consumption': referredUser?.minConsumption || '-',
          'Referral Reward Value': referredUser?.referralRewardValue || '-',
          'Percentage Per Scan': referredUser?.percentagePerScan || '-',
          'Referral Period': referredUser?.referralPeriod || '-',
          'Total Points': referredUser?.totalpoints || '-',
          'Value': referredUser?.value || '-',
          'Total Money': referredUser?.totalmoney || '-',
          'Refer Date': referredUser?.referDate || '-'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting referred user data at index ${index}:`, error, referredUser);
        return {
          'Transaction ID': `Error_${index}`,
          'Error': 'Failed to format referred user data'
        };
      }
    });
  }, []);

  // Format Scan Per Product Details data based on Apps Script function
  const formatScanPerProductDetailsData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatScanPerProductDetailsData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script scanPerProductdetails function
    const columnOrder = [
      'QR Value',
      'QR Type', 
      'Product Name',
      'Product Code',
      'Product Type Name',
      'User Type Name',
      'User ID',
      'User Name',
      'Phone',
      'Scan Action',
      'Purchase Points',
      'Sells Points',
      'Use Points',
      'Backword Points',
      'Backword Money',
      'Wheel Points',
      'Shop Name',
      'Created At'
    ];
    
    return data.map((scanData, index) => {
      try {
        // Create object in the exact order defined above
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'QR Value': scanData?.qrValue || '_',
          'QR Type': scanData?.qrType || '_',
          'Product Name': scanData?.productName || '-',
          'Product Code': scanData?.productCode || '-',
          'Product Type Name': scanData?.productTypeName || '_',
          'User Type Name': scanData?.userTypeName || '-',
          'User ID': scanData?.userId || '_',
          'User Name': scanData?.userName || '_',
          'Phone': scanData?.phone || '_',
          'Scan Action': scanData?.scanAction || '_',
          'Purchase Points': scanData?.purchasePoints || '_',
          'Sells Points': scanData?.sellsPoints || '_',
          'Use Points': scanData?.usePoints || '_',
          'Backword Points': scanData?.backwordPoints || '_',
          'Backword Money': scanData?.backwordMoney || '_',
          'Wheel Points': scanData?.wheelPoints || '_',
          'Shop Name': scanData?.shopName || '_',
          'Created At': scanData?.createdAt || '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting scan per product details data at index ${index}:`, error, scanData);
        return {
          'QR Value': `Error_${index}`,
          'Error': 'Failed to format scan per product details data'
        };
      }
    });
  }, []);

  // Format Scans per product per user type data based on Apps Script function
  const formatScansPerProductPerUserTypeData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatScansPerProductPerUserTypeData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script scansDetailsPerUserType function
    const columnOrder = [
      'Product Name',
      'Product Code',
      'User Type Name',
      'Scan Action',
      'Counter',
      'Product Type Name'
    ];
    
    return data.map((scanData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Product Name': scanData?.productName ?? '-',
          'Product Code': scanData?.productCode ?? '-',
          'User Type Name': scanData?.userTypeName ?? '_',
          'Scan Action': scanData?.scanAction ?? '_',
          'Counter': scanData?.counter ?? '_',
          'Product Type Name': scanData?.productTypeName ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting scan details data at index ${index}:`, error, scanData);
        return {
          'Product Name': `Error_${index}`,
          'Error': 'Failed to format scan details data'
        };
      }
    });
  }, []);

  // Format Warehouse Scan In/Out data based on Apps Script function
  const formatWarehouseScanInOutData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatWarehouseScanInOutData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script warehouseScanInOut function
    const columnOrder = [
      'Product ID',
      'Product Name',
      'Product Code',
      'Count',
      'Warehouse ID',
      'Warehouse Name',
      'Created At'
    ];
    
    return data.map((scanData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Product ID': scanData?.productId ?? '-',
          'Product Name': scanData?.productDetails?.name ?? '_',
          'Product Code': scanData?.productDetails?.code ?? '_',
          'Count': scanData?.count ?? '_',
          'Warehouse ID': scanData?.warehouseId ?? '_',
          'Warehouse Name': scanData?.warehouseDetails?.name ?? '_',
          'Created At': scanData?.productDetails?.createdAt ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting warehouse scan data at index ${index}:`, error, scanData);
        return {
          'Product ID': `Error_${index}`,
          'Error': 'Failed to format warehouse scan data'
        };
      }
    });
  }, []);

  // Format Configuration Result Per Product data based on Apps Script function
  const formatConfigurationResultPerProductData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatConfigurationResultPerProductData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script configuration function
    const columnOrder = [
      'ID',
      'Product Name',
      'Product Code',
      'Configure Counter',
      'Product Type Name'
    ];
    
    return data.map((configData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'ID': configData?._id ?? '-',
          'Product Name': configData?.productName ?? '-',
          'Product Code': configData?.productCode ?? '-',
          'Configure Counter': configData?.configureCounter ?? '_',
          'Product Type Name': configData?.productTypeName ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting configuration data at index ${index}:`, error, configData);
        return {
          'ID': `Error_${index}`,
          'Error': 'Failed to format configuration data'
        };
      }
    });
  }, []);

  // Format Deactivated Incentive QR data based on Apps Script function
  const formatDeactivatedIncentiveQRData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatDeactivatedIncentiveQRData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script deactivatedIncentiveQR function
    // Order matches the exact sequence from the Apps Script: QRCode, QRType, disableIncentiveType, OrderCode, SKUCode, SKUName, WarehouseID, WarehouseName, UserName, createdAt date, createdAt time
    const columnOrder = [
      'QR Code',
      'QR Type', 
      'Disable Incentive Type',
      'Order Code',
      'SKU Code',
      'SKU Name',
      'Warehouse ID',
      'Warehouse Name',
      'User Name',
      'Created Date',
      'Created Time'
    ];
    
    return data.map((qrData, index) => {
      try {
        const orderedObject: any = {};
        
        // Format date and time similar to Apps Script
        let createdDate = '_';
        let createdTime = '_';
        
        if (qrData?.createdAt) {
          try {
            const dateObj = new Date(qrData.createdAt);
            createdDate = dateObj.toLocaleDateString();
            createdTime = dateObj.toLocaleTimeString();
          } catch (dateError) {
            console.warn('Error formatting date:', dateError);
            createdDate = '_';
            createdTime = '_';
          }
        }
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'QR Code': qrData?.QRCode ?? '-',
          'QR Type': qrData?.QRType ?? '-',
          'Disable Incentive Type': qrData?.disableIncentiveType ?? '_',
          'Order Code': qrData?.OrderCode ?? '_',
          'SKU Code': qrData?.SKUCode ?? '_',
          'SKU Name': qrData?.SKUName ?? '_',
          'Warehouse ID': qrData?.WarehouseID ?? '_',
          'Warehouse Name': qrData?.WarehouseName ?? '_',
          'User Name': qrData?.UserName ?? '_',
          'Created Date': createdDate,
          'Created Time': createdTime
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting deactivated incentive QR data at index ${index}:`, error, qrData);
        return {
          'QR Code': `Error_${index}`,
          'Error': 'Failed to format deactivated incentive QR data'
        };
      }
    });
  }, []);

  // Format Order Scanout From Warehouse data based on Apps Script function
  const formatOrderScanoutFromWarehouseData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatOrderScanoutFromWarehouseData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script orderScanoutFromWarehouse function
    const columnOrder = [
      'Value',
      'Warehouse Details',
      'Scaned',
      'Date'
    ];
    
    return data.map((scanData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Value': scanData?.value ?? '-',
          'Warehouse Details': scanData?.warehouseDetails ?? '-',
          'Scaned': scanData?.SCANED ?? '_',
          'Date': scanData?.date ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting order scanout data at index ${index}:`, error, scanData);
        return {
          'Value': `Error_${index}`,
          'Error': 'Failed to format order scanout data'
        };
      }
    });
  }, []);

  // Format Warehouse Quantity Aggregation data based on Apps Script function
  const formatWarehouseQuantityAggregationData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatWarehouseQuantityAggregationData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script warehouseQuantityAggregation function
    const columnOrder = [
      'Warehouse ID',
      'Warehouse Name',
      'Warehouse User Name',
      'SKU Name',
      'Quantity',
      'Delivery Note QR Code',
      'Stamped For User ID',
      'Stamped For User Name',
      'Stamped User Type',
      'Scanned',
      'Scan Date Time',
      'Scan User ID',
      'Scan Performed By',
      'Scan User Type',
      'Aggregation Date Time'
    ];
    
    return data.map((warehouseData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Warehouse ID': warehouseData?.warehouseId ?? '-',
          'Warehouse Name': warehouseData?.warehouseName ?? '_',
          'Warehouse User Name': warehouseData?.warehouseUserName ?? '_',
          'SKU Name': warehouseData?.skuName ?? '_',
          'Quantity': warehouseData?.quantity ?? '_',
          'Delivery Note QR Code': warehouseData?.deliveryNoteQrCode ?? '_',
          'Stamped For User ID': warehouseData?.stampedForUserId ?? '_',
          'Stamped For User Name': warehouseData?.stampedForUserName ?? '_',
          'Stamped User Type': warehouseData?.stampedUserType ?? '_',
          'Scanned': warehouseData?.scanned ?? '_',
          'Scan Date Time': warehouseData?.scanDateTime ?? '_',
          'Scan User ID': warehouseData?.scanUserId ?? '_',
          'Scan Performed By': warehouseData?.scanPerformedBy ?? '_',
          'Scan User Type': warehouseData?.scanUserType ?? '_',
          'Aggregation Date Time': warehouseData?.aggregationDateTime ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting warehouse quantity aggregation data at index ${index}:`, error, warehouseData);
        return {
          'Warehouse ID': `Error_${index}`,
          'Error': 'Failed to format warehouse quantity aggregation data'
        };
      }
    });
  }, []);

  // Format Paymob data based on Apps Script function
  const formatPaymobData = useCallback((data: any): any[] => {
    // Handle nested API response structure like the Apps Script
    let extractedData = data;
    
    // Extract docs array from the response structure
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      if (data.docs && Array.isArray(data.docs)) {
        extractedData = data.docs;
      } else if (Array.isArray(data.data)) {
        extractedData = data.data;
      } else if (Array.isArray(data.body)) {
        extractedData = data.body;
      }
    }
    
    if (!Array.isArray(extractedData)) {
      console.warn('formatPaymobData: Expected array, got:', typeof extractedData);
      return [];
    }
    
    // Define columns based on the Apps Script paymob function - exact order
    const columnOrder = [
      'Transaction ID',
      'User ID', 
      'User Name',
      'MSISDN',
      'Governorate',
      'District',
      'User Type',
      'Transaction Type',
      'Provider',
      'Balance Before',
      'Provider Fees',
      'Admin Fees',
      'Net',
      'Amount',
      'Status',
      'Created At'
    ];
    
    return extractedData.map((transactionData, index) => {
      try {
        // Create object in the exact order defined above
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Transaction ID': transactionData?.transactionId ?? '-',
          'User ID': transactionData?.userId ?? '-',
          'User Name': transactionData?.userName ?? '_',
          'MSISDN': transactionData?.msisdn ?? '_',
          'Governorate': transactionData?.governorate ?? '_',
          'District': transactionData?.district ?? '_',
          'User Type': transactionData?.userType ?? '_',
          'Transaction Type': transactionData?.transactionType ?? '_',
          'Provider': transactionData?.provider ?? '_',
          'Balance Before': transactionData?.balanceBefore ?? '_',
          'Provider Fees': transactionData?.providerFees ?? '_',
          'Admin Fees': transactionData?.adminFees ?? '_',
          'Net': transactionData?.net ?? '_',
          'Amount': transactionData?.amount ?? '_',
          'Status': transactionData?.status ?? '_',
          'Created At': transactionData?.createdAt ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting paymob data at index ${index}:`, error, transactionData);
        return {
          'Transaction ID': `Error_${index}`,
          'Error': 'Failed to format paymob data'
        };
      }
    });
  }, []);

  // Format Reset Logs data based on Apps Script function
  const formatResetLogsData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatResetLogsData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script transactionLog function - exact order
    const columnOrder = [
      'ID',
      'Warehouse Name',
      'User ID', 
      'User Name',
      'User Phone',
      'Action',
      'QR Code Type',
      'QR Code Value',
      'Product Type',
      'SKU Code',
      'Order QR',
      'Created Date',
      'Created Time'
    ];
    
    const rows: any[] = [];
    
    return data.reduce((acc, logData, index) => {
      try {
        // No action filtering here since API returns all data and Google Apps Script filters on display
        
        // Handle nested SKU codes structure (like Google Apps Script loop)
        const skuCodes = logData?.sKUCode || [];
        if (Array.isArray(skuCodes) && skuCodes.length > 0) {
          skuCodes.forEach((sku, skuIndex) => {
            const orderedObject: any = {};
            
            // Format dates similar to Apps Script
            let createdDate = '_';
            let createdTime = '_';
            
            if (logData?.createdAt) {
              try {
                const date = new Date(logData.createdAt);
                createdDate = date.toLocaleDateString();
                createdTime = date.toLocaleTimeString();
              } catch (dateError) {
                console.warn('Date formatting error:', dateError);
              }
            }
            
            // Map data to columns based on Apps Script structure
            const dataMapping: { [key: string]: any } = {
              'ID': logData?._id ?? '-',
              'Warehouse Name': logData?.warehouseName ?? '_',
              'User ID': logData?.userId ?? '-',
              'User Name': logData?.userName ?? '_',
              'User Phone': logData?.userPhone ?? '_',
              'Action': logData?.action ?? '_',
              'QR Code Type': logData?.QrcodeType ?? '_',
              'QR Code Value': logData?.QrcodeValue ?? '_',
              'Product Type': logData?.ProductType ?? '_',
              'SKU Code': sku?.code ?? '_',
              'Order QR': logData?.orderQr || 'Null',
              'Created Date': createdDate,
              'Created Time': createdTime
            };
            
            // Build object in the specified order
            columnOrder.forEach(column => {
              orderedObject[column] = dataMapping[column];
            });
            
            acc.push(orderedObject);
          });
        } else {
          // Handle case where there are no SKU codes
          const orderedObject: any = {};
          
          // Format dates
          let createdDate = '_';
          let createdTime = '_';
          
          if (logData?.createdAt) {
            try {
              const date = new Date(logData.createdAt);
              createdDate = date.toLocaleDateString();
              createdTime = date.toLocaleTimeString();
            } catch (dateError) {
              console.warn('Date formatting error:', dateError);
            }
          }
          
          const dataMapping: { [key: string]: any } = {
            'ID': logData?._id ?? '-',
            'Warehouse Name': logData?.warehouseName ?? '_',
            'User ID': logData?.userId ?? '-',
            'User Name': logData?.userName ?? '_',
            'User Phone': logData?.userPhone ?? '_',
            'Action': logData?.action ?? '_',
            'QR Code Type': logData?.QrcodeType ?? '_',
            'QR Code Value': logData?.QrcodeValue ?? '_',
            'Product Type': logData?.ProductType ?? '_',
            'SKU Code': '_',
            'Order QR': logData?.orderQr || 'Null',
            'Created Date': createdDate,
            'Created Time': createdTime
          };
          
          columnOrder.forEach(column => {
            orderedObject[column] = dataMapping[column];
          });
          
          acc.push(orderedObject);
        }
        
        return acc;
      } catch (error) {
        console.error(`Error formatting reset logs data at index ${index}:`, error, logData);
        return acc;
      }
    }, [] as any[]);
  }, [dynamicParams]);

  // Format Return System Returns data based on Apps Script returnQr function
  const formatReturnSystemReturnsData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatReturnSystemReturnsData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script returnQr function - exact order
    const columnOrder = [
      'Warehouse',
      'Warehouse User ID',
      'Warehouse User',
      'Created At',
      'Product Type',
      'Products Name',
      'Products Code',
      'ID',
      'Distributor In',
      'Distributor In Date',
      'Distributor Out',
      'Distributor Out Date',
      'Retailer In',
      'Retailer In Date',
      'Retailer Out',
      'Retailer Out Date',
      'Electrician USE',
      'Electrician USE Date'
    ];
    
    return data.map((returnData, index) => {
      try {
        // Initialize scan tracking variables as in Apps Script
        let distributorIn = '-';
        let distributorInDate = '-';
        let distributorOut = '-';
        let distributorOutDate = '-';
        let retailerOut = '-';
        let retailerOutDate = '-';
        let retailerIn = '-';
        let retailerInDate = '-';
        let electricianUSE = '-';
        let electricianUSEDate = '-';
        
        // Process scanArray as in Apps Script
        if (returnData?.scanArray && Array.isArray(returnData.scanArray)) {
          for (let scan of returnData.scanArray) {
            if (scan.action === 'SCANIN' && scan.userType === 'Distributer') {
              distributorIn = 'true';
              distributorInDate = scan.createdAt;
            } else if (scan.action === 'SCANOUT' && scan.userType === 'Distributer') {
              distributorOut = 'true';
              distributorOutDate = scan.createdAt;
            } else if (scan.action === 'SCANIN' && scan.userType === 'POD') {
              retailerIn = 'true';
              retailerInDate = scan.createdAt;
            } else if (scan.action === 'SCANOUT' && scan.userType === 'POD') {
              retailerOut = 'true';
              retailerOutDate = scan.createdAt;
            } else if (scan.action === 'SCANUSE' && scan.userType === 'Electrician') {
              electricianUSE = 'true';
              electricianUSEDate = scan.createdAt;
            }
          }
        }
        
        // Process scanArrayOfParent as in Apps Script
        if (returnData?.scanArrayOfParent && Array.isArray(returnData.scanArrayOfParent)) {
          for (let scanP of returnData.scanArrayOfParent) {
            if (scanP.action === 'SCANIN' && scanP.userType?.name === 'Distributer') {
              distributorIn = 'true';
              distributorInDate = scanP.createdAt;
            } else if (scanP.action === 'SCANOUT' && scanP.userType?.name === 'Distributer') {
              distributorOut = 'true';
              distributorOutDate = scanP.createdAt;
            } else if (scanP.action === 'SCANIN' && scanP.userType?.name === 'POD') {
              retailerIn = 'true';
              retailerInDate = scanP.createdAt;
            } else if (scanP.action === 'SCANOUT' && scanP.userType?.name === 'POD') {
              retailerOut = 'true';
              retailerOutDate = scanP.createdAt;
            }
          }
        }
        
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Warehouse': returnData?.warehouse ?? '_',
          'Warehouse User ID': returnData?.warehouseUserId ?? '_',
          'Warehouse User': returnData?.warehouseUser ?? '_',
          'Created At': returnData?.createdAt ?? '_',
          'Product Type': returnData?.productType ?? '_',
          'Products Name': returnData?.productsName ?? '_',
          'Products Code': returnData?.productsCode ?? '_',
          'ID': returnData?._id ?? '_',
          'Distributor In': distributorIn ?? '_',
          'Distributor In Date': distributorInDate ?? '_',
          'Distributor Out': distributorOut ?? '_',
          'Distributor Out Date': distributorOutDate ?? '_',
          'Retailer In': retailerIn ?? '_',
          'Retailer In Date': retailerInDate ?? '_',
          'Retailer Out': retailerOut ?? '_',
          'Retailer Out Date': retailerOutDate ?? '_',
          'Electrician USE': electricianUSE ?? '_',
          'Electrician USE Date': electricianUSEDate ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting return system returns data at index ${index}:`, error, returnData);
        return {
          'ID': `Error_${index}`,
          'Error': 'Failed to format return system returns data'
        };
      }
    });
  }, []);

  // Format Return System Incentive data based on Apps Script incentive function
  const formatReturnSystemIncentiveData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatReturnSystemIncentiveData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script incentive function - exact order
    const columnOrder = [
      'User Type',
      'User ID',
      'Product Type',
      'Products Name',
      'Products Code',
      'Quantity',
      'Purchase Points',
      'Sells Points',
      'Use Points',
      'Backward Points',
      'Backward Money',
      'Wheel Points',
      'Action',
      'Scan Created At',
      'Data Created At',
      'ID'
    ];
    
    // Process data similar to Apps Script - iterate through each data item and its scanArray
    const rows: any[] = [];
    
    data.forEach((incentiveData, dataIndex) => {
      if (incentiveData?.scanArray && Array.isArray(incentiveData.scanArray)) {
        incentiveData.scanArray.forEach((scan: any, scanIndex: number) => {
          try {
            const orderedObject: any = {};
            
            // Map data to columns based on Apps Script structure
            const dataMapping: { [key: string]: any } = {
              'User Type': scan?.userType ?? '_',
              'User ID': scan?.userId ?? '_',
              'Product Type': incentiveData?.productType ?? '_',
              'Products Name': incentiveData?.productsName ?? '_',
              'Products Code': incentiveData?.productsCode ?? '_',
              'Quantity': 1, // Fixed value as in Apps Script
              'Purchase Points': scan?.purchasePoints ?? 0,
              'Sells Points': scan?.sellsPoints ?? 0,
              'Use Points': scan?.usePoints ?? 0,
              'Backward Points': scan?.backwordPoints ?? '_',
              'Backward Money': scan?.backwordMoney ?? '_',
              'Wheel Points': scan?.wheelPoints ?? '_',
              'Action': scan?.action ?? '_',
              'Scan Created At': scan?.createdAt ?? '_',
              'Data Created At': incentiveData?.createdAt ?? '_',
              'ID': incentiveData?._id ?? '_'
            };
            
            // Build object in the specified order
            columnOrder.forEach(column => {
              orderedObject[column] = dataMapping[column];
            });
            
            rows.push(orderedObject);
          } catch (error) {
            console.error(`Error formatting return system incentive data at data index ${dataIndex}, scan index ${scanIndex}:`, error, scan);
            rows.push({
              'ID': `Error_${dataIndex}_${scanIndex}`,
              'Error': 'Failed to format return system incentive data'
            });
          }
        });
      }
    });
    
    return rows;
  }, []);

  // Format Gifts data based on Apps Script function
  const formatGiftsData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatGiftsData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script gifts function
    const columnOrder = [
      'ID',
      'User ID',
      'User Name',
      'User Type',
      'Phone',
      'Governorate',
      'District',
      'Gift Name',
      'Gift Value',
      'Wheel Name',
      'Milestone Name',
      'Milestone Count',
      'Status',
      'Created At'
    ];
    
    return data.map((giftData, index) => {
      try {
        // Create object in the exact order defined above
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure with special wheel logic
        const dataMapping: { [key: string]: any } = {
          'ID': giftData?._id || '-',
          'User ID': giftData?.userId || '-',
          'User Name': giftData?.userName || '_',
          'User Type': giftData?.userType || '_',
          'Phone': giftData?.phone || '_',
          'Governorate': giftData?.governorate || '_',
          'District': giftData?.district || '_',
          'Gift Name': giftData?.giftName || '_',
          'Gift Value': giftData?.giftValue || '_',
          'Wheel Name': (giftData?.isReferralWheel === true) ? (giftData?.wheelName || '_') : '_',
          'Milestone Name': giftData?.milestoneName || '_',
          'Milestone Count': giftData?.milestoneCount || '_',
          'Status': giftData?.status || '_',
          'Created At': giftData?.createdAt || '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting gift data at index ${index}:`, error, giftData);
        return {
          'ID': `Error_${index}`,
          'Error': 'Failed to format gift data'
        };
      }
    });
  }, []);

  // Format Invoice Photos data based on Apps Script function
  const formatInvoicePhotosData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatInvoicePhotosData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script invoices function - exact order
    const columnOrder = [
      'ID',
      'User ID',
      'Full Name',
      'Phone',
      'User Type',
      'Governorate',
      'District',
      'Images',
      'Created At'
    ];
    
    return data.map((invoiceData, index) => {
      try {
        const orderedObject: any = {};
        
        // Special logic for User Type: convert "Electrician" to "EndUser"
        let userType = invoiceData?.userData?.userType?.name ?? '_';
        if (userType === 'Electrician') {
          userType = 'EndUser';
        }
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'ID': invoiceData?._id ?? '_',
          'User ID': invoiceData?.userData?.userId ?? '-',
          'Full Name': invoiceData?.userData?.fullName ?? '-',
          'Phone': invoiceData?.userData?.phone ?? '_',
          'User Type': userType,
          'Governorate': invoiceData?.userData?.governate?.name ?? '_',
          'District': invoiceData?.userData?.dirstrict?.name ?? '_',
          'Images': invoiceData?.images ?? '_',
          'Created At': invoiceData?.createdAt ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting invoice photos data at index ${index}:`, error, invoiceData);
        return {
          'ID': `Error_${index}`,
          'Error': 'Failed to format invoice photos data'
        };
      }
    });
  }, []);

  // Format Joining Requests data based on Apps Script function
  const formatJoiningRequestsData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatJoiningRequestsData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script JoiningRequests function - exact order
    const columnOrder = [
      'ID',
      'Date',
      'Time',
      'First Name',
      'Last Name',
      'Phone',
      'Governorate',
      'District',
      'Status'
    ];
    
    return data.map((requestData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'ID': requestData?._id ?? '-',
          'Date': requestData?.date ?? '-',
          'Time': requestData?.time ?? '_',
          'First Name': requestData?.firstName ?? '_',
          'Last Name': requestData?.lastName ?? '_',
          'Phone': requestData?.phone ?? '_',
          'Governorate': requestData?.governorate ?? '_',
          'District': requestData?.district ?? '_',
          'Status': requestData?.status ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting joining request data at index ${index}:`, error, requestData);
        return {
          'ID': `Error_${index}`,
          'Error': 'Failed to format joining request data'
        };
      }
    });
  }, []);

  // Format Deposit Amount Log data based on Apps Script function
  const formatDepositAmountLogData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatDepositAmountLogData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script depositAmountLog function - exact order
    const columnOrder = [
      'ID',
      'User ID',
      'User Name',
      'Transaction Type',
      'Value',
      'Previous Balance',
      'Current Balance',
      'Comment',
      'Created By',
      'Created At'
    ];
    
    return data.map((transactionData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'ID': transactionData?._id ?? '-',
          'User ID': transactionData?.userId ?? '_',
          'User Name': transactionData?.userName ?? '_',
          'Transaction Type': transactionData?.transactionType ?? '_',
          'Value': transactionData?.value ?? '_',
          'Previous Balance': transactionData?.previousBalance ?? '_',
          'Current Balance': transactionData?.currentBalance ?? '_',
          'Comment': transactionData?.comment ?? '_',
          'Created By': transactionData?.createdBy ?? '_',
          'Created At': transactionData?.createdAt ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting deposit amount log data at index ${index}:`, error, transactionData);
        return {
          'ID': `Error_${index}`,
          'Error': 'Failed to format deposit amount log data'
        };
      }
    });
  }, []);

  // Format Deposit Amount data based on Apps Script function with validation and POST functionality
  const formatDepositAmountData = (inputData: any): any => {
    // This function handles both validation and POST request for deposit amount
    const validateAndSubmitDeposit = async () => {
      try {
        // Use the depositTransactions state instead of dynamicParams
        const selectedData = [];
        
        // Double-check validation (safeguard)
        if (depositTransactions.length === 0) {
          throw new Error('Please add at least one transaction before sending the operation.');
        }
        
        // Process each transaction (validation already done in executeApi)
        for (let i = 0; i < depositTransactions.length; i++) {
          const tx = depositTransactions[i];
          
          const numericValue = Number(tx.value);
          // Get login email from localStorage - try multiple possible keys
          const createdBy = localStorage.getItem('user_email') || 
                           localStorage.getItem('email') || 
                           localStorage.getItem('api_username') || 
                           localStorage.getItem('google_email') ||
                           localStorage.getItem('userEmail') ||
                           'qara-sheets';
          const transactionData = {
            userId: tx.userId.toString(),
            transactionType: tx.transactionType,
            value: numericValue,
            comment: tx.comment || '',
            createdBy: createdBy
          };
          selectedData.push(transactionData);
        }
        
        // Confirmation dialog simulation
        const confirmed = window.confirm('Are you sure to make this operation?');
        if (!confirmed) {
          return { message: 'Operation cancelled by user' };
        }
        
        // Prepare API data
        const apiData = { transactions: selectedData };
        
        // Make POST request
        const response = await apiService.submitDepositAmount(apiData);
        
        if (response?.status === 201) {
          // Extract message from response body - handle different response structures
          let successMessage = 'Deposit amount processed successfully';
          
          if (response.data) {
            // Try multiple possible message fields in the response
            successMessage = response.data.body?.message || 
                           successMessage;
          }
          
          alert(successMessage);
          
          // Clear transactions after successful submission
          setDepositTransactions([]);
          
          return response.data;
        } else {
          throw new Error(response?.data?.description || 'Failed to process deposit amount');
        }
        
      } catch (error: any) {
        console.error('Deposit Amount Error:', error);
        
        // Extract error message from API response - handle different error structures
        let errorMessage = 'Failed to process deposit amount';
        
        if (error.response?.data) {
          // Try multiple possible error message fields in the response
          errorMessage = error.response.data.body?.message || 
                       error.response.data.description || 
                       error.response.data.error ||
                       errorMessage;
        } else if (error.message) {
          // Parse error message if it contains JSON
          if (error.message.includes('{')) {
            try {
              const jsonStart = error.message.indexOf('{');
              const jsonEnd = error.message.lastIndexOf('}');
              if (jsonStart !== -1 && jsonEnd !== -1) {
                const jsonStr = error.message.substring(jsonStart, jsonEnd + 1);
                const parsedError = JSON.parse(jsonStr);
                errorMessage = parsedError.description || parsedError.message || error.message;
              }
            } catch (parseError) {
              errorMessage = error.message;
            }
          } else {
            errorMessage = error.message;
          }
        }
        
        alert('Error: ' + errorMessage);
        throw error;
      }
    };
    
    // Return the validation and submission function
    return validateAndSubmitDeposit();
  };

  // Format Wallet Transactions data based on Apps Script function
  const formatWalletTransactionsData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatWalletTransactionsData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script walletTransaction function
    const columnOrder = [
      'Index',
      'Transaction Type',
      'Value',
      'Balance Before',
      'Balance After',
      'Created At',
      'User ID',
      'Full Name',
      'User Type'
    ];
    
    return data.map((transactionData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Index': (index + 1),
          'Transaction Type': transactionData?.transactionType ?? '-',
          'Value': transactionData?.value ?? '_',
          'Balance Before': transactionData?.balanceBefore ?? '_',
          'Balance After': transactionData?.balanceAfter ?? '_',
          'Created At': transactionData?.createdAt ?? '_',
          'User ID': transactionData?.user_id ?? '_',
          'Full Name': `${transactionData?.firstName} ${transactionData?.lastName}` ,
          'User Type': transactionData?.userType ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting wallet transactions data at index ${index}:`, error, transactionData);
        return {
          'Index': `Error_${index}`,
          'Error': 'Failed to format wallet transactions data'
        };
      }
    });
  }, []);

  // Format User Details Per Product Per Action data and other QR codes endpoints
  const formatUserDetailsPerProductPerActionData = useCallback((data: any): any[] => {
    
    // Handle nested API response structure like the Apps Script
    let extractedData = data;
    
    // If data is an object, extract the docs array from the nested structure
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      // Look for docs in the first key of the object (like Apps Script does)
      const keys = Object.keys(data);
      if (keys.length > 0) {
        const firstKey = keys[0];
        if (data[firstKey] && data[firstKey].docs && Array.isArray(data[firstKey].docs)) {
          extractedData = data[firstKey].docs;
        } else if (Array.isArray(data[firstKey])) {
          // Sometimes the data might be directly in the first key without 'docs'
          extractedData = data[firstKey];
        }
      }
    }
    
    if (!Array.isArray(extractedData)) {
      console.warn('Expected array for QR codes data, received:', typeof extractedData);
      return [];
    }
    
    // Define columns based on the Apps Script userScanAnalysis function
    const columnOrder = [
      'User Type Name',
      'Product Name', 
      'Product Code',
      'Phone',
      'Shop Name',
      'Counter',
      'Wallet Type',
      'Purchase Cash',
      'Sells Cash',
      'Use Cash',
      'Purchase Points',
      'Sells Points',
      'Use Points',
      'Backward Points',
      'Backward Money',
      'Wheel Points',
      'User ID',
      'Scan Action',
      'User Name',
      'Governorate',
      'District',
      'Created At',
      'Product Type Name'
    ];
    
    return extractedData.map((scanData, index) => {
      try {
        // If the data doesn't have the expected structure for User Details Per Product Per Action,
        // return the data as-is with basic formatting
        if (!scanData.hasOwnProperty('userTypeName') && !scanData.hasOwnProperty('productName')) {
          return scanData; // Return raw data for other QR codes endpoints
        }
        
        const formattedRow: any = {};
        
        formattedRow[columnOrder[0]] = scanData?.userTypeName ?? '-';
        formattedRow[columnOrder[1]] = scanData?.productName ?? '-';
        formattedRow[columnOrder[2]] = scanData?.productCode ?? '-';
        formattedRow[columnOrder[3]] = scanData?.phone ?? '_';
        formattedRow[columnOrder[4]] = scanData?.shopName ?? '_';
        formattedRow[columnOrder[5]] = scanData?.counter ?? '_';
        formattedRow[columnOrder[6]] = scanData?.WalletType ?? "_";
        formattedRow[columnOrder[7]] = scanData?.purchaseCash ?? "_";
        formattedRow[columnOrder[8]] = scanData?.sellsCash ?? "_";
        formattedRow[columnOrder[9]] = scanData?.useCash ?? "_";
        formattedRow[columnOrder[10]] = scanData?.purchasePoints ?? '_';
        formattedRow[columnOrder[11]] = scanData?.sellsPoints ?? '_';
        formattedRow[columnOrder[12]] = scanData?.usePoints ?? '_';
        formattedRow[columnOrder[13]] = scanData?.backwordPoints ?? '_';
        formattedRow[columnOrder[14]] = scanData?.backwordMoney ?? '_';
        formattedRow[columnOrder[15]] = scanData?.wheelPoints ?? '_';
        formattedRow[columnOrder[16]] = scanData?.userId ?? '_';
        formattedRow[columnOrder[17]] = scanData?.scanAction ?? '_';
        formattedRow[columnOrder[18]] = scanData?.userName ?? '_';
        formattedRow[columnOrder[19]] = scanData?.governorate ?? '_';
        formattedRow[columnOrder[20]] = scanData?.district ?? '_';
        formattedRow[columnOrder[21]] = scanData?.createdAt ?? '_';
        formattedRow[columnOrder[22]] = scanData?.productTypeName ?? '_';
        
        return formattedRow;
      } catch (error) {
        console.error(`Error formatting QR codes data at index ${index}:`, error, scanData);
        return scanData; // Return raw data if formatting fails
      }
    });
  }, []);

  const renderTableView = (data: any): JSX.Element | null => {
    if (!data) return null;
    
    try {
      // Format data based on endpoint type
      let processedData = data;
      if (selectedEndpoint?.endpoint === 'Users Points' && Array.isArray(data)) {
        processedData = formatUsersPointsData(data);
      } else if (selectedEndpoint?.endpoint === 'Referral Users' && Array.isArray(data)) {
        processedData = formatReferralUsersData(data);
      } else if (selectedEndpoint?.endpoint === 'Referred Users' && Array.isArray(data)) {
        processedData = formatReferredUsersData(data);
      } else if (selectedEndpoint?.endpoint === 'User Details Per Product Per Action') {
        processedData = formatUserDetailsPerProductPerActionData(data);
      } else if (selectedEndpoint?.endpoint === 'Configuration Result Per Product' && Array.isArray(data)) {
        processedData = formatConfigurationResultPerProductData(data);
      } else if (selectedEndpoint?.endpoint === 'Deactivated Incentive QR' && Array.isArray(data)) {
        processedData = formatDeactivatedIncentiveQRData(data);
      } else if (selectedEndpoint?.endpoint === 'Order Scanout From Warehouse' && Array.isArray(data)) {
        processedData = formatOrderScanoutFromWarehouseData(data);
      } else if (selectedEndpoint?.endpoint === 'Scans per product per user type' && Array.isArray(data)) {
        processedData = formatScansPerProductPerUserTypeData(data);
      } else if (selectedEndpoint?.endpoint === 'Scan Per Product details' && Array.isArray(data)) {
        processedData = formatScanPerProductDetailsData(data);
      } else if (selectedEndpoint?.endpoint === 'Gifts' && Array.isArray(data)) {
        processedData = formatGiftsData(data);
      } else if (selectedEndpoint?.endpoint === 'Warehouse Scan In/Out' && Array.isArray(data)) {
        processedData = formatWarehouseScanInOutData(data);
      } else if (selectedEndpoint?.endpoint === 'Warehouse Quantity Aggregation' && Array.isArray(data)) {
        processedData = formatWarehouseQuantityAggregationData(data);
      } else if (selectedEndpoint?.endpoint === 'Wallet Transactions') {
        // Handle nested data structure like the Apps Script (data.body)
        let walletData = data;
        if (data && data.body && Array.isArray(data.body)) {
          walletData = data.body;
        } else if (Array.isArray(data)) {
          walletData = data;
        } else {
          console.warn('Wallet Transactions: Expected array or object with body array, got:', typeof data, data);
          walletData = [];
        }
        
        if (Array.isArray(walletData)) {
          processedData = formatWalletTransactionsData(walletData);
        }
      } else if (selectedEndpoint?.endpoint === 'Deposit Amount') {
        // Handle Deposit Amount as success message display
        if (data && (data.message || data.data)) {
          processedData = [{
            'Operation': 'Deposit Amount',
            'Status': 'Success',
            'Message': data.message || data.data?.message || 'Operation completed successfully',
            'Timestamp': new Date().toLocaleString()
          }];
        } else {
          processedData = data;
        }
      } else if (selectedEndpoint?.endpoint === 'Paymob') {
        // Handle Paymob data formatting
        processedData = formatPaymobData(data);
      } else if (selectedEndpoint?.endpoint === 'invoice-photos' && Array.isArray(data)) {
        processedData = formatInvoicePhotosData(data);
      } else if (selectedEndpoint?.endpoint === 'joining-requests' && Array.isArray(data)) {
        processedData = formatJoiningRequestsData(data);
      } else if (selectedEndpoint?.endpoint === 'reset-logs' && Array.isArray(data)) {
        processedData = formatResetLogsData(data);
      } else if (selectedEndpoint?.endpoint === 'Deposit Amount Log' && Array.isArray(data)) {
        processedData = formatDepositAmountLogData(data);
      } else if (selectedEndpoint?.endpoint === 'Return System' && Array.isArray(data)) {
        processedData = formatReturnSystemReturnsData(data);
      } else if (selectedEndpoint?.endpoint === 'Return System - Returns' && Array.isArray(data)) {
        processedData = formatReturnSystemReturnsData(data);
      } else if (selectedEndpoint?.endpoint === 'Incentive' && Array.isArray(data)) {
        processedData = formatReturnSystemIncentiveData(data);
      } else if (selectedEndpoint?.category === 'QR Codes') {
        // Generic handling for other QR codes endpoints
        processedData = formatUserDetailsPerProductPerActionData(data);
      }
      
      
      // Ensure we have valid data to render
      if (!processedData || (Array.isArray(processedData) && processedData.length === 0)) {
        return (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: '#666'
          }}>
            No data available to display
          </div>
        );
      }
      
      // Use the same TableViewer component that's used in the new window
      // This ensures consistent data processing and rendering
      return (
        <div style={{
          width: '100%',
          maxHeight: '600px',
          overflow: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          background: 'white'
        }}>
          <TableViewer 
            data={processedData} 
            endpoint={selectedEndpoint?.endpoint || 'Unknown'}
            timestamp={new Date().toISOString()}
            showCloseButton={false}
          />
        </div>
      );
    } catch (error) {
      console.error('Error rendering table view:', error);
      return (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#d32f2f',
          border: '1px solid #ffcdd2',
          borderRadius: '4px',
          backgroundColor: '#ffebee'
        }}>
          Error rendering table data. Please check the console for details.
        </div>
      );
    }
  };
  


  useEffect(() => {
    loadEndpoints();
    loadUserTypes();
    
    // Cleanup function to cancel any pending requests when component unmounts
    return () => {
      if (currentRequestController) {
        currentRequestController.abort();
      }
    };
  }, []);
  
  // Cleanup request on component unmount or when currentRequestController changes
  useEffect(() => {
    return () => {
      if (currentRequestController) {
        currentRequestController.abort();
      }
    };
  }, [currentRequestController]);

  // Filter endpoints based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredEndpoints(endpoints);
      return;
    }

    const filtered: { [key: string]: ApiEndpoint[] } = {};
    const query = searchQuery.toLowerCase();

    Object.entries(endpoints).forEach(([category, categoryEndpoints]) => {
      const matchingEndpoints = categoryEndpoints.filter(endpoint =>
        endpoint.endpoint.toLowerCase().includes(query) ||
        endpoint.description.toLowerCase().includes(query) ||
        category.toLowerCase().includes(query)
      );

      if (matchingEndpoints.length > 0) {
        filtered[category] = matchingEndpoints;
      }
    });

    setFilteredEndpoints(filtered);
  }, [endpoints, searchQuery]);

  const loadEndpoints = async () => {
    try {
      const result = await apiService.getEndpoints();
      setEndpoints(result.data);
      
      // Set first category as active
      const firstCategory = Object.keys(result.data)[0];
      setActiveCategory(firstCategory);
    } catch (error) {
      // Silently handle endpoint loading error
    }
  };

  const loadUserTypes = async () => {
    try {
      const result = await apiService.getUserTypes();
      setUserTypes(result.data as string[]);

    } catch (error) {
      // Fallback to default user types
      setUserTypes(['Admin', 'user']);
    }
  };

  const executeApi = async () => {
    if (!selectedEndpoint) return;

    // Special validation for Deposit Amount endpoint
    if (selectedEndpoint.endpoint === 'Deposit Amount') {
      
      if (depositTransactions.length === 0) {
        alert('Please add at least one transaction before sending the operation.');
        return;
      }
      
      // Validate each transaction
      const invalidTransactions: string[] = [];
      depositTransactions.forEach((tx, index) => {
        if (!tx.userId || !tx.transactionType || !tx.value) {
          invalidTransactions.push(`Transaction ${index + 1}: Missing required fields`);
        } else if (Number(tx.value) <= 0 || isNaN(Number(tx.value))) {
          invalidTransactions.push(`Transaction ${index + 1}: Invalid amount (must be positive number)`);
        }
      });
      
      if (invalidTransactions.length > 0) {
        alert(`Please fix the following issues:\n\n${invalidTransactions.join('\n')}`);
        return;
      }
    }

    // Validate required parameters before making the API call
    const endpointPath = selectedEndpoint.endpoint;
    const paramConfig = getParameterConfig(endpointPath);
    
    // Check for required parameters
    const missingRequiredParams: string[] = [];
    
    if (paramConfig && paramConfig.length > 0) {
      paramConfig.forEach(param => {
        if (param.required) {
          const value = dynamicParams[param.name];
          if (!value || (typeof value === 'string' && value.trim() === '') || value === 'loading') {
            missingRequiredParams.push(param.label);
          }
        }
      });
    }
    
    // Show error if required parameters are missing
    if (missingRequiredParams.length > 0) {
      setError(`Please choose the following required parameters: ${missingRequiredParams.join(', ')}`);
      return;
    }

    // Cancel any existing request
    if (currentRequestController) {
      currentRequestController.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    setCurrentRequestController(abortController);

    setLoading(true);
    setError('');
    setResponse(null);
    setLoadingProgress('');

    try {
      // Map the dashboard endpoint to the actual service method
      let result: { data: any };
      
      if (endpointPath === 'Users Types') {
        // Use dynamic params from form inputs for Users Types
        const parsedParams = JSON.parse(convertDynamicParamsToJson());
        result = await apiService.getUsersExcel(parsedParams);
      } else if (endpointPath === 'Users Points') {
        // Auto-load all pages - no user parameters needed
        const originalConsoleLog = console.log;
        console.log = (...args) => {
          const message = args.join(' ');
          if (message.includes('Fetched page') || message.includes('Total records') || message.includes('Pagination complete')) {
            setLoadingProgress(message);
          }
          originalConsoleLog(...args);
        };
        
        try {
          result = await apiService.getProductionFeeds({});
        } finally {
          console.log = originalConsoleLog;
          setLoadingProgress('');
        }
      } else if (endpointPath === 'Users') {
        // Users endpoint - load all users without parameters
        result = await apiService.getUsersWithAcceptedBy({});
      } else if (endpointPath === 'Referral Users') {
        // Use dynamic params from form inputs for Referral Users
        const parsedParams = JSON.parse(convertDynamicParamsToJson());
        result = await apiService.callDynamicAPI(selectedEndpoint.originalPath, parsedParams);
      } else {
        // Use dynamic params from form inputs for all other endpoints
        const parsedParams = JSON.parse(convertDynamicParamsToJson());
        
        // // Add reasonable default limits to prevent large data requests
        // if (!parsedParams.limit) {
        //   parsedParams.limit = 100; // Default to smaller limit for better performance
        // } else if (parsedParams.limit > 1000) {
        //   parsedParams.limit = 1000; // Cap at 1000 to prevent excessive load
        // }

        //qr codes category
        if (endpointPath === 'User Details Per Product Per Action') {
          result = await apiService.getUserScanAnalysis(parsedParams);
        } else if (endpointPath === 'Scans per product per user type') {
          result = await apiService.getScansDetailsPerUserType(parsedParams);
        } else if (endpointPath === 'Scan Per Product details') {
          result = await apiService.getScanPerProductDetails(parsedParams);
          } else if (endpointPath === 'Warehouse Scan In/Out') {
          result = await apiService.getWarehouseScanInOutDetails(parsedParams);
          } else if (endpointPath === 'Deactivated Incentive QR') {
          result = await apiService.getDeactivatedIncentiveQR(parsedParams);
          } else if (endpointPath === 'Configuration Result Per Product') {
          result = await apiService.getConfigurationSheetCounterPerProduct(parsedParams);
          } else if (endpointPath === 'Warehouse Quantity Aggregation') {
          result = await apiService.getWarehouseQuantityAggregation(parsedParams);
          } else if (endpointPath === 'Order Scanout From Warehouse') {
          result = await apiService.getOrderScanoutFromWarehouse(parsedParams);
          
        // Wallet category endpoints
        } else if (endpointPath === 'Wallet Transactions') {
          result = await apiService.getWalletTransactionSheet(parsedParams);
        } else if (endpointPath === 'Deliver Cash') {
          result = await apiService.getDeliverCash(parsedParams);
        } else if (endpointPath === 'Deliver Transfer') {
          result = await apiService.getDeliverTransfer(parsedParams);
        } else if (endpointPath === 'Welcome Bonus') {
          result = await apiService.getWelcomeBonus(parsedParams);
          
        // Payment/Paymob endpoints
        } else if (endpointPath === 'Paymob') {
          result = await apiService.getPaymentLogs(parsedParams);
          
        // Invoice endpoints
        } else if (endpointPath === 'invoice-photos') {
          result = await apiService.getInvoicePhotosSheet(parsedParams);
          
        // Transaction Logs endpoints
        } else if (endpointPath === 'reset-logs') {
          result = await apiService.getResetLogs(parsedParams);
          
        // Joining Requests endpoints
        } else if (endpointPath === 'joining-requests') {
          result = await apiService.getJoiningRequests(parsedParams);
          
        // Return System endpoints
        } else if (endpointPath === 'Return System') {
          // Convert dateType parameter as in Apps Script
          let convertedParams = { ...parsedParams };
          if (convertedParams.dateType === 'Return Date') {
            convertedParams.dateType = 'returnDate';
          } else if (convertedParams.dateType === 'Transaction Date') {
            convertedParams.dateType = 'TransactionDate';
          }
          result = await apiService.getReturnSystemSheet(convertedParams);
        } else if (endpointPath === 'Return System - Returns') {
          // Convert dateType parameter as in Apps Script
          let convertedParams = { ...parsedParams };
          if (convertedParams.dateType === 'Return Date') {
            convertedParams.dateType = 'returnDate';
          } else if (convertedParams.dateType === 'Transaction Date') {
            convertedParams.dateType = 'TransactionDate';
          }
          result = await apiService.getReturnSystemSheet(convertedParams);
        } else if (endpointPath === 'Incentive') {
          // Convert dateType parameter as in Apps Script
          let convertedParams = { ...parsedParams };
          if (convertedParams.dateType === 'Return Date') {
            convertedParams.dateType = 'returnDate';
          } else if (convertedParams.dateType === 'Transaction Date') {
            convertedParams.dateType = 'TransactionDate';
          }
          result = await apiService.getReturnSystemSheet(convertedParams);
          
        // Deposit Amount endpoints
        } else if (endpointPath === 'Deposit Amount') {
          // For Deposit Amount, handle as POST operation with validation
          result = await formatDepositAmountData({ userData: [] });
        } else if (endpointPath === 'Deposit Amount Log') {
          result = await apiService.getDepositAmountOperations(parsedParams);
          
        // Gifts endpoints
        } else if (endpointPath === 'Gifts') {
          result = await apiService.getGiftsSheet(parsedParams);
          
        } else {
          // Use dynamic API call for other endpoints
          result = await apiService.callDynamicAPI(selectedEndpoint.originalPath, parsedParams);
        }
      }
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      setResponse(result.data);
      
      // Save successful data to cache
      if (selectedEndpoint) {
        setEndpointDataCache(prev => ({
          ...prev,
          [selectedEndpoint.endpoint]: {
            data: result.data,
            params: { ...dynamicParams },
            timestamp: new Date().toISOString()
          }
        }));
      }
    } catch (err: any) {
      // Don't show error if request was cancelled
      if (err.name === 'AbortError' || err.message?.includes('aborted')) {
        return;
      }
      
      let errorMessage = 'Unable to load report data. Please try again.';
      
      // Better error messages based on the error type
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Try reducing the limit or check your connection.';
      } else if (err.response?.status === 504) {
        errorMessage = 'Server timeout. The request is taking too long. Try reducing the data limit.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else if (err.response?.status === 409) {
        // Handle business logic errors (Conflict)
        if (err.response?.data?.description) {
          errorMessage = `Business Rule Error: ${err.response.data.description}`;
        } else if (err.response?.data?.message) {
          errorMessage = `Business Rule Error: ${err.response.data.message}`;
        } else {
          errorMessage = 'Business rule validation failed. Please check your input parameters.';
        }
      } else if (err.response?.status === 400) {
        // Handle bad request errors
        if (err.response?.data?.description) {
          errorMessage = `Invalid Request: ${err.response.data.description}`;
        } else if (err.response?.data?.message) {
          errorMessage = `Invalid Request: ${err.response.data.message}`;
        } else {
          errorMessage = 'Invalid request parameters. Please check your input.';
        }
      } else if (err.response?.data?.description) {
        errorMessage = err.response.data.description;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
      
      // Save error to cache as well
      if (selectedEndpoint) {
        setEndpointDataCache(prev => ({
          ...prev,
          [selectedEndpoint.endpoint]: {
            data: null,
            params: { ...dynamicParams },
            timestamp: new Date().toISOString(),
            error: errorMessage
          }
        }));
      }
    } finally {
      setLoading(false);
      setCurrentRequestController(null);
    }
  };

  const convertToCSV = (data: any): string => {
    if (!data) return '';
    
    let arrayData: any[] = [];
    
    // Extract array from different data structures
    if (Array.isArray(data)) {
      arrayData = data;
    } else if (typeof data === 'object' && data !== null) {
      // Check for common data properties
      const dataKeys = ['docs', 'data', 'items', 'results', 'records', 'list'];
      
      for (const key of dataKeys) {
        if (data[key] && Array.isArray(data[key])) {
          arrayData = data[key];
          break;
        }
      }
      
      // If no array found, check all properties
      if (arrayData.length === 0) {
        const arrayProperties = Object.entries(data).filter(([_, value]) => Array.isArray(value));
        if (arrayProperties.length === 1) {
          arrayData = arrayProperties[0][1] as any[];
        } else {
          // Treat the object itself as a single row
          arrayData = [data];
        }
      }
    } else {
      // Primitive value
      return `Value\n"${String(data)}"`;
    }
    
    if (arrayData.length === 0) return 'No data available';
    
    // Get all unique headers from all objects
    const allKeys = new Set<string>();
    arrayData.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach(key => allKeys.add(key));
      }
    });
    
    const headers = Array.from(allKeys);
    
    // Convert to CSV format
    const csvRows: string[] = [];
    
    // Add header row
    csvRows.push(headers.map(header => `"${header}"`).join(','));
    
    // Add data rows
    arrayData.forEach(item => {
      const row = headers.map(header => {
        const value = item[header];
        return formatValueForCSV(value);
      });
      csvRows.push(row.join(','));
    });
    
    return csvRows.join('\n');
  };
  
  const formatValueForCSV = (value: any): string => {
    if (value === null || value === undefined) return '""';
    
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        if (value.length === 0) return '""';
        return `"${value.join('; ')}"`;
      }
      return `"${JSON.stringify(value)}"`;
    }
    
    if (typeof value === 'boolean') {
      return `"${value ? 'Yes' : 'No'}"`;
    }
    
    if (typeof value === 'string') {
      // Handle date strings
      const dateCheck = new Date(value);
      if (!isNaN(dateCheck.getTime()) && value.includes('-')) {
        return `"${dateCheck.toLocaleDateString()}"`;
      }
      // Escape quotes in strings
      return `"${value.replace(/"/g, '""')}"`;
    }
    
    return `"${String(value)}"`;
  };

  const downloadResponse = () => {
    if (!response || !selectedEndpoint) return;

    // Use formatted data based on endpoint type
    let dataToExport = response;
    if (selectedEndpoint.endpoint === 'Users Points' && Array.isArray(response)) {
      dataToExport = formatUsersPointsData(response);
    } else if (selectedEndpoint.endpoint === 'Referral Users' && Array.isArray(response)) {
      dataToExport = formatReferralUsersData(response);
    } else if (selectedEndpoint.endpoint === 'Referred Users' && Array.isArray(response)) {
      dataToExport = formatReferredUsersData(response);
    } else if (selectedEndpoint.endpoint === 'User Details Per Product Per Action') {
      dataToExport = formatUserDetailsPerProductPerActionData(response);
    } else if (selectedEndpoint.endpoint === 'Configuration Result Per Product' && Array.isArray(response)) {
      dataToExport = formatConfigurationResultPerProductData(response);
    } else if (selectedEndpoint.endpoint === 'Deactivated Incentive QR' && Array.isArray(response)) {
      dataToExport = formatDeactivatedIncentiveQRData(response);
    } else if (selectedEndpoint.endpoint === 'Order Scanout From Warehouse' && Array.isArray(response)) {
      dataToExport = formatOrderScanoutFromWarehouseData(response);
    } else if (selectedEndpoint.endpoint === 'Scans per product per user type' && Array.isArray(response)) {
      dataToExport = formatScansPerProductPerUserTypeData(response);
    } else if (selectedEndpoint.endpoint === 'Scan Per Product details' && Array.isArray(response)) {
      dataToExport = formatScanPerProductDetailsData(response);
    } else if (selectedEndpoint.endpoint === 'Gifts' && Array.isArray(response)) {
      dataToExport = formatGiftsData(response);
    } else if (selectedEndpoint.endpoint === 'Warehouse Scan In/Out' && Array.isArray(response)) {
      dataToExport = formatWarehouseScanInOutData(response);
    } else if (selectedEndpoint.endpoint === 'Warehouse Quantity Aggregation' && Array.isArray(response)) {
      dataToExport = formatWarehouseQuantityAggregationData(response);
    } else if (selectedEndpoint.endpoint === 'Wallet Transactions') {
      let walletData = response;
      if (response && response.body && Array.isArray(response.body)) {
        walletData = response.body;
      } else if (Array.isArray(response)) {
        walletData = response;
      } else {
        walletData = [];
      }
      dataToExport = Array.isArray(walletData) ? formatWalletTransactionsData(walletData) : [];
    } else if (selectedEndpoint.endpoint === 'Deposit Amount') {
      // Handle Deposit Amount success message for CSV export
      if (response && (response.message || response.body)) {
        dataToExport = [{
          'Operation': 'Deposit Amount',
          'Status': 'Success',
          'Message': response.message || response.body?.message || 'Operation completed successfully',
          'Timestamp': new Date().toLocaleString()
        }];
      } else {
        dataToExport = response;
      }
    } else if (selectedEndpoint.endpoint === 'Paymob') {
      dataToExport = formatPaymobData(response);
    } else if (selectedEndpoint.endpoint === 'invoice-photos' && Array.isArray(response)) {
      dataToExport = formatInvoicePhotosData(response);
    } else if (selectedEndpoint.endpoint === 'Return System' && Array.isArray(response)) {
      dataToExport = formatReturnSystemReturnsData(response); // Default to returns format
    } else if (selectedEndpoint.endpoint === 'Return System - Returns' && Array.isArray(response)) {
      dataToExport = formatReturnSystemReturnsData(response);
    } else if (selectedEndpoint.endpoint === 'Incentive' && Array.isArray(response)) {
      dataToExport = formatReturnSystemIncentiveData(response);
    } else if (selectedEndpoint.endpoint === 'joining-requests' && Array.isArray(response)) {
      dataToExport = formatJoiningRequestsData(response);
    } else if (selectedEndpoint.endpoint === 'reset-logs' && Array.isArray(response)) {
      dataToExport = formatResetLogsData(response);
    } else if (selectedEndpoint.endpoint === 'Deposit Amount Log' && Array.isArray(response)) {
      dataToExport = formatDepositAmountLogData(response);
    } 
    // else if (selectedEndpoint.category === 'QR Codes') {
    //   dataToExport = formatUserDetailsPerProductPerActionData(response);
    // }

    const csvData = convertToCSV(dataToExport);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedEndpoint.endpoint.replace(/\//g, '_')}_${Date.now()}.csv`;
    link.click();
    
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async () => {
    if (!response) return;
    
    try {
      await navigator.clipboard.writeText(JSON.stringify(response, null, 2));
      alert('Report data copied to clipboard!');
    } catch (err) {

    }
  };

  // Clear cached data for current endpoint
  const clearCurrentEndpointData = () => {
    if (!selectedEndpoint) return;
    
    // Remove from cache
    setEndpointDataCache(prev => {
      const newCache = { ...prev };
      delete newCache[selectedEndpoint.endpoint];
      return newCache;
    });
    
    // Clear current display
    setResponse(null);
    setError('');
    setDynamicParams({});
  };



  const openTableInNewWindow = () => {
    if (!response || !selectedEndpoint) return;
    
    // // Log Users data structure specifically 
    // if (selectedEndpoint.endpoint === 'Users') {
    //   if (Array.isArray(response)) {
    //   } else if (typeof response === 'object' && response) {
    //     // Check for paginated structure
    //     if (response.docs && Array.isArray(response.docs)) {
    //     }
    //   }
    // }

    // Format the data correctly based on the endpoint
    let dataToSend = response;
    if (selectedEndpoint.endpoint === 'Users Points' && Array.isArray(response)) {
      dataToSend = formatUsersPointsData(response);
    } else if (selectedEndpoint.endpoint === 'Referral Users' && Array.isArray(response)) {
      dataToSend = formatReferralUsersData(response);
    } else if (selectedEndpoint.endpoint === 'Referred Users' && Array.isArray(response)) {
      dataToSend = formatReferredUsersData(response);
    } else if (selectedEndpoint.endpoint === 'User Details Per Product Per Action') {
      dataToSend = formatUserDetailsPerProductPerActionData(response);
    } else if (selectedEndpoint.endpoint === 'Configuration Result Per Product' && Array.isArray(response)) {
      dataToSend = formatConfigurationResultPerProductData(response);
    } else if (selectedEndpoint.endpoint === 'Deactivated Incentive QR' && Array.isArray(response)) {
      dataToSend = formatDeactivatedIncentiveQRData(response);
    } else if (selectedEndpoint.endpoint === 'Order Scanout From Warehouse' && Array.isArray(response)) {
      dataToSend = formatOrderScanoutFromWarehouseData(response);
    } else if (selectedEndpoint.endpoint === 'Scans per product per user type' && Array.isArray(response)) {
      dataToSend = formatScansPerProductPerUserTypeData(response);
    } else if (selectedEndpoint.endpoint === 'Scan Per Product details' && Array.isArray(response)) {
      dataToSend = formatScanPerProductDetailsData(response);
    } else if (selectedEndpoint.endpoint === 'Gifts' && Array.isArray(response)) {
      dataToSend = formatGiftsData(response);
    } else if (selectedEndpoint.endpoint === 'Warehouse Scan In/Out' && Array.isArray(response)) {
      dataToSend = formatWarehouseScanInOutData(response);
    } else if (selectedEndpoint.endpoint === 'Warehouse Quantity Aggregation' && Array.isArray(response)) {
      dataToSend = formatWarehouseQuantityAggregationData(response);
    } else if (selectedEndpoint.endpoint === 'Wallet Transactions') {
      let walletData = response;
      if (response && response.body && Array.isArray(response.body)) {
        walletData = response.body;
      } else if (Array.isArray(response)) {
        walletData = response;
      } else {
        walletData = [];
      }
      dataToSend = Array.isArray(walletData) ? formatWalletTransactionsData(walletData) : [];
    } else if (selectedEndpoint.endpoint === 'Deposit Amount') {
      // Handle Deposit Amount success message for new window
      if (response && (response.message || response.body)) {
        dataToSend = [{
          'Operation': 'Deposit Amount',
          'Status': 'Success',
          'Message': response.message || response.body?.message || 'Operation completed successfully',
          'Timestamp': new Date().toLocaleString()
        }];
      } else {
        dataToSend = response;
      }
    } else if (selectedEndpoint.endpoint === 'Paymob') {
      dataToSend = formatPaymobData(response);
    } else if (selectedEndpoint.endpoint === 'invoice-photos' && Array.isArray(response)) {
      dataToSend = formatInvoicePhotosData(response);
    } else if (selectedEndpoint.endpoint === 'Return System' && Array.isArray(response)) {
      dataToSend = formatReturnSystemReturnsData(response); // Default to returns format
    } else if (selectedEndpoint.endpoint === 'Return System - Returns' && Array.isArray(response)) {
      dataToSend = formatReturnSystemReturnsData(response);
    } else if (selectedEndpoint.endpoint === 'Incentive' && Array.isArray(response)) {
      dataToSend = formatReturnSystemIncentiveData(response);
    } else if (selectedEndpoint.endpoint === 'joining-requests' && Array.isArray(response)) {
      dataToSend = formatJoiningRequestsData(response);
    } else if (selectedEndpoint.endpoint === 'reset-logs' && Array.isArray(response)) {
      dataToSend = formatResetLogsData(response);
    } else if (selectedEndpoint.endpoint === 'Deposit Amount Log' && Array.isArray(response)) {
      dataToSend = formatDepositAmountLogData(response);
    } 
    // else if (selectedEndpoint.category === 'QR Codes') {
    //   dataToSend = formatUserDetailsPerProductPerActionData(response);
    // }

    // Store data in localStorage immediately for instant access
    const storageKey = `table-viewer-data-${Date.now()}`;
    const messageData = {
      data: dataToSend,
      endpoint: selectedEndpoint.endpoint,
      timestamp: new Date().toISOString(),
      storageKey: storageKey
    };

    // Try to store in localStorage, but handle quota exceeded gracefully
    let usePostMessage = false;
    try {
      // Clean up old entries first to free up space
      const cleanupOldEntries = () => {
        const now = Date.now();
        const maxAge = 60000; // 1 minute
        
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith('table-viewer-data-')) {
            const timestamp = parseInt(key.replace('table-viewer-data-', ''));
            if (now - timestamp > maxAge) {
              localStorage.removeItem(key);
            }
          }
        }
      };
      
      cleanupOldEntries();
      
      // For large datasets, store only metadata and use postMessage
      if (JSON.stringify(messageData).length > 4000000) { // ~4MB limit
        usePostMessage = true;
        
        // Store only minimal metadata
        const metadata = {
          endpoint: selectedEndpoint.endpoint,
          timestamp: new Date().toISOString(),
          recordCount: Array.isArray(response) ? response.length : 1,
          storageKey: storageKey
        };
        localStorage.setItem(storageKey, JSON.stringify(metadata));
      } else {
        localStorage.setItem(storageKey, JSON.stringify(messageData));
      }
      
    } catch (error) {
      console.error('localStorage quota exceeded, using postMessage only:', error);
      usePostMessage = true;
    }

    // Open window with storage key in URL for instant data access
    // Include endpoint in the URL to make it more unique
    const windowName = `table-viewer-${selectedEndpoint.endpoint.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`;
    const newWindow = window.open(
      `/table-viewer?dataKey=${storageKey}&endpoint=${encodeURIComponent(selectedEndpoint.endpoint)}`,
      windowName,
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,status=no'
    );

    if (newWindow) {

      let messageSent = false;
      let timeoutId: NodeJS.Timeout;
      let intervalId: NodeJS.Timeout;

      // Listen for messages from new window
      const handleMessage = (event: MessageEvent) => {
        
        if (event.origin !== window.location.origin) return;
        
        // Handle ready signal
        if (event.data?.type === 'table-viewer-ready' && !messageSent) {
          messageSent = true;
          try {
            newWindow.postMessage(messageData, window.location.origin);
          } catch (error) {
            console.error('Error sending data to new window:', error);
          }
          cleanup();
        }

        // Handle direct data request
        if (event.data?.type === 'table-viewer-request-data') {
          try {
            newWindow.postMessage(messageData, window.location.origin);
          } catch (error) {
            console.error('Error sending data on request:', error);
          }
        }
      };

      const cleanup = () => {
        window.removeEventListener('message', handleMessage);
        if (timeoutId) clearTimeout(timeoutId);
        if (intervalId) clearInterval(intervalId);
      };

      window.addEventListener('message', handleMessage);

      // Immediate attempt to send data (in case window loads very fast)
      setTimeout(() => {
        if (!messageSent) {
          try {
            newWindow.postMessage(messageData, window.location.origin);
          } catch (error) {
          }
        }
      }, 100);

      // Fallback: send message after 3 seconds even if no ready signal
      timeoutId = setTimeout(() => {
        if (!messageSent) {
          messageSent = true;
          try {
            newWindow.postMessage(messageData, window.location.origin);
          } catch (error) {
            console.error('Fallback send failed:', error);
          }
        }
        cleanup();
      }, 3000);

      // Also try sending periodically for the first few seconds
      let attempts = 0;
      intervalId = setInterval(() => {
        attempts++;
        if (!messageSent && attempts <= 6) {
          try {
            newWindow.postMessage(messageData, window.location.origin);
          } catch (error) {
          }
        } else {
          if (intervalId) clearInterval(intervalId);
        }
      }, 500);

      // Clean up listener if window is closed
      const checkClosed = setInterval(() => {
        if (newWindow.closed) {
          clearInterval(checkClosed);
          cleanup();
        }
      }, 1000);
    }
  };

  return (
    <div className="api-explorer">
      <div className="explorer-header">
        <h1> Reports Explorer</h1>
        <p>Access and view your business data from different reports and systems</p>
      </div>

      <div className="explorer-content">
        {/* Sidebar with endpoints */}
        <div className="endpoints-sidebar">
          <h3><FontAwesomeIcon icon={faFolder} style={{ marginRight: '8px', color: '#f39c12' }} /> Reports</h3>
          
          {/* Search input */}
          <div className="search-container">
            <div className="search-input-wrapper">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                placeholder="Search endpoints..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>
          
          {Object.entries(filteredEndpoints).map(([category, categoryEndpoints]) => (
            <div key={category} className="endpoint-category">
              <button
                className={`category-header ${activeCategory === category ? 'active' : ''}`}
                onClick={() => setActiveCategory(activeCategory === category ? '' : category)}
              >
                <FontAwesomeIcon icon={faFolderOpen} style={{ marginRight: '6px', color: '#e67e22' }} /> {category.toUpperCase()}
                <span className="endpoint-count">({categoryEndpoints.length})</span>
              </button>
              
              {activeCategory === category && (
                <div className="category-endpoints">
                  {categoryEndpoints.map((endpoint) => (
                    <button
                      key={endpoint.endpoint}
                      className={`endpoint-item ${selectedEndpoint?.endpoint === endpoint.endpoint ? 'selected' : ''}`}
                      onClick={() => handleEndpointSelection(endpoint)}
                    >
                      <div className="endpoint-name">
                        {endpoint.endpoint}
                        {endpointDataCache[endpoint.endpoint] && (
                          <span className="cached-indicator" title="Data cached">
                            <FontAwesomeIcon icon={faSave} style={{ fontSize: '12px', color: '#27ae60' }} />
                          </span>
                        )}
                      </div>
                      <div className="endpoint-desc">{endpoint.description}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Main content */}
        <div className="api-console">
          {selectedEndpoint ? (
            <>
              <div className="console-header">
                <h2><FontAwesomeIcon icon={faCog} style={{ marginRight: '8px', color: '#6c757d' }} /> {selectedEndpoint.endpoint}</h2>
                <p>{selectedEndpoint.description}</p>
                <div className="endpoint-info">
                  <span className="endpoint-category">Report Type: {selectedEndpoint.category}</span>
                </div>
              </div>

              <div className="console-body">
                <div className="input-section">
                  <div className="section-header">
                    <h3><FontAwesomeIcon icon={faEdit} style={{ marginRight: '8px', color: '#17a2b8' }} /> Report Filters</h3>
                  </div>
                  
                  <div className="dynamic-params">
                    {selectedEndpoint?.endpoint === 'Users Points' && (
                      <div className="performance-tip" style={{
                        backgroundColor: '#e8f5e8',
                        color: '#2d5a2d',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #4caf50'
                      }}>
                        <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '6px', color: '#3498db' }} /> <strong>Users Points Data:</strong> This endpoint automatically loads ALL user production feeds with points data.<br/>
                        <FontAwesomeIcon icon={faRocket} style={{ marginRight: '6px', color: '#e74c3c' }} /> <strong>Automatic Loading:</strong> No parameters needed - system fetches all pages automatically (1000 records per page).<br/>
                        <FontAwesomeIcon icon={faHourglassHalf} style={{ marginRight: '6px', color: '#f39c12' }} /> <strong>Progress:</strong> Monitor the browser console and loading button for real-time progress updates.
                      </div>
                    )}
                    {selectedEndpoint?.endpoint === 'Users' && (
                      <div className="performance-tip" style={{
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #2196f3'
                      }}>
                        <FontAwesomeIcon icon={faUsers} style={{ marginRight: '6px', color: '#17a2b8' }} /> <strong>Users Data:</strong> This endpoint loads all users with their complete profile information.<br/>
                        <FontAwesomeIcon icon={faRocket} style={{ marginRight: '6px', color: '#e74c3c' }} /> <strong>Ready to Load:</strong> No filters needed - system will fetch all user records automatically.<br/>
                        <FontAwesomeIcon icon={faClipboardList} style={{ marginRight: '6px', color: '#9b59b6' }} /> <strong>Data Includes:</strong> User profiles, contact details, location info, and account status.
                      </div>
                    )}
                    {selectedEndpoint && getParameterConfig(selectedEndpoint.endpoint).length === 0 ? (
                      <div className="no-parameters-message" style={{
                        backgroundColor: '#f8f9fa',
                        color: '#495057',
                        padding: '20px',
                        borderRadius: '8px',
                        textAlign: 'center',
                        border: '1px solid #dee2e6'
                      }}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px', color: '#28a745' }} /> <strong>No Filters Required</strong><br/>
                        This endpoint loads data automatically without any additional parameters.
                      </div>
                    ) : (
                      selectedEndpoint && getParameterConfig(selectedEndpoint.endpoint).map((param) => (
                        <div key={param.name} className="param-group">
                          <label htmlFor={param.name}>
                            {param.label}
                            {param.required && <span className="required">*</span>}
                          </label>
                          {param.type === 'boolean' ? (
                            <input
                              id={param.name}
                              type="checkbox"
                            checked={
                              param.name === 'allowPagination' 
                                ? (dynamicParams[param.name] === undefined || dynamicParams[param.name] === true || dynamicParams[param.name] === 'true')
                                : (dynamicParams[param.name] === true || dynamicParams[param.name] === 'true')
                            }
                            onChange={(e) => updateDynamicParams(param.name, e.target.checked)}
                              className="param-checkbox"
                            />
                          ) : param.type === 'select' ? (
                            <select
                              id={param.name}
                              value={typeof dynamicParams[param.name] === 'string' ? dynamicParams[param.name] as string : ''}
                              onChange={(e) => updateDynamicParams(param.name, e.target.value)}
                              className="param-select"
                            >
                              <option value="">Select {param.label}</option>
                              {param.options?.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              id={param.name}
                              type={param.type}
                              value={typeof dynamicParams[param.name] === 'string' ? dynamicParams[param.name] as string : ''}
                              onChange={(e) => updateDynamicParams(param.name, e.target.value)}
                              placeholder={param.placeholder}
                              className="param-input"
                            />
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Special UI for Deposit Amount endpoint */}
                  {selectedEndpoint?.endpoint === 'Deposit Amount' && (
                    <div className="deposit-transactions-section" style={{
                      marginTop: '20px',
                      padding: '20px',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      backgroundColor: '#f8f9fa'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h4 style={{ margin: '0', color: '#495057' }}>
                          <FontAwesomeIcon icon={faEdit} style={{ marginRight: '8px', color: '#17a2b8' }} />
                          Deposit Transactions
                        </h4>
                        <div>
                          <button
                            onClick={addDepositTransaction}
                            style={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              marginRight: '10px'
                            }}
                          >
                            <FontAwesomeIcon icon={faPlay} style={{ marginRight: '6px' }} />
                            Add Transaction
                          </button>
                          {depositTransactions.length > 0 && (
                            <>
                              <button
                                onClick={() => {
                                  // Force a state refresh
                                  setDepositTransactions([...depositTransactions]);
                                }}
                                style={{
                                  backgroundColor: '#17a2b8',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  marginRight: '10px'
                                }}
                              >
                                <FontAwesomeIcon icon={faRedo} style={{ marginRight: '6px' }} />
                                Refresh
                              </button>
                              <button
                                onClick={clearAllDepositTransactions}
                                style={{
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} style={{ marginRight: '6px' }} />
                                Clear All
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {depositTransactions.length === 0 ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '40px',
                          color: '#6c757d',
                          fontStyle: 'italic',
                          border: '2px dashed #dee2e6',
                          borderRadius: '8px',
                          backgroundColor: '#f8f9fa'
                        }}>
                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '2em', marginBottom: '10px', color: '#ffc107' }} />
                          <br />
                          <strong>No transactions added yet!</strong>
                          <br />
                          Click the "Add Transaction" button above to create your first deposit transaction.
                          <br />
                          <small style={{ color: '#6c757d', marginTop: '10px', display: 'block' }}>
                            You need at least one transaction to send the operation.
                          </small>
                        </div>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            <thead>
                              <tr style={{ backgroundColor: '#007bff', color: 'white' }}>
                                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>User ID</th>
                                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>Transaction Type</th>
                                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>Deposit Amount</th>
                                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>Comments</th>
                                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: '100px' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {depositTransactions.map((tx, index) => (
                                <tr key={tx.id} style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white' }}>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <input
                                      type="text"
                                      value={tx.userId}
                                      onChange={(e) => updateDepositTransaction(tx.id, 'userId', e.target.value)}
                                      placeholder="Enter user ID"
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <select
                                      value={tx.transactionType}
                                      onChange={(e) => updateDepositTransaction(tx.id, 'transactionType', e.target.value)}
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px'
                                      }}
                                    >
                                      <option value="">Select Type</option>
                                      <option value="DEDUCTIONINCENTIVE">DEDUCTION INCENTIVE</option>
                                      <option value="CONTINUITYINCENTIVE">CONTINUITY INCENTIVE</option>
                                      <option value="EXTRAINCENTIVE">EXTRA INCENTIVE</option>
                                    </select>
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <input
                                      type="number"
                                      value={tx.value}
                                      onChange={(e) => updateDepositTransaction(tx.id, 'value', e.target.value)}
                                      placeholder="Amount"
                                      min="0"
                                      step="0.01"
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <input
                                      type="text"
                                      value={tx.comment}
                                      onChange={(e) => updateDepositTransaction(tx.id, 'comment', e.target.value)}
                                      placeholder="Optional comments"
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                    <button
                                      onClick={() => removeDepositTransaction(tx.id)}
                                      style={{
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}
                                      title="Remove transaction"
                                    >
                                      <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div style={{ marginTop: '10px', fontSize: '14px', color: '#6c757d' }}>
                            Total Transactions: {depositTransactions.length}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>

                <div className="action-section">
                  <button 
                    onClick={() => {
                      executeApi();
                    }} 
                    disabled={loading || (selectedEndpoint?.endpoint === 'Deposit Amount' && depositTransactions.length === 0)}
                    className="execute-btn"
                    title={
                      selectedEndpoint?.endpoint === 'Deposit Amount' && depositTransactions.length === 0 
                        ? 'Please add at least one transaction before sending the operation' 
                        : ''
                    }
                  >
                    {loading ? (
                      selectedEndpoint?.endpoint === 'Users Points' && loadingProgress ? 
                        <><FontAwesomeIcon icon={faHourglassHalf} style={{ marginRight: '6px', color: '#f39c12' }} /> {loadingProgress}</> :
                        <><FontAwesomeIcon icon={faHourglassHalf} style={{ marginRight: '6px', color: '#f39c12' }} /> Loading Report...</>
                    ) : (response ? 
                        <><FontAwesomeIcon icon={faRedo} style={{ marginRight: '6px' }} /> Reload Data</> : 
                        selectedEndpoint?.endpoint === 'Deposit Amount' ? 
                          <><FontAwesomeIcon icon={faPlay} style={{ marginRight: '6px' }} /> Send Operation</> :
                          <><FontAwesomeIcon icon={faPlay} style={{ marginRight: '5px' }} /> Load Data</>
                    )}
                  </button>
                  
                  {selectedEndpoint && endpointDataCache[selectedEndpoint.endpoint] && !loading && (
                    <button 
                      onClick={clearCurrentEndpointData}
                      className="clear-btn"
                      style={{
                        marginLeft: '10px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title="Clear cached data for this endpoint"
                    >
                      <FontAwesomeIcon icon={faTrash} style={{ marginRight: '6px' }} /> Clear Data
                    </button>
                  )}
                  
                  {loading && (
                    <button 
                      onClick={() => {
                        if (currentRequestController) {
                          currentRequestController.abort();
                          setCurrentRequestController(null);
                          setLoading(false);
                          setError('Request cancelled by user');
                        }
                      }}
                      className="cancel-btn"
                      style={{
                        marginLeft: '10px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} style={{ marginRight: '6px', color: '#e74c3c' }} /> Cancel
                    </button>
                  )}
                </div>

                {error && (
                  <div className="error-section">
                    <h3><FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '8px', color: '#e74c3c' }} /> Unable to Load Report</h3>
                    <div className="error-content">
                      {error}
                    </div>
                  </div>
                )}

                {response && (
                  <div className="response-section">
                    <div className="response-header">
                      <h3><FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '8px', color: '#27ae60' }} /> Report Data</h3>
                      <div className="response-controls">
                        <div className="response-actions">
                          <button onClick={openTableInNewWindow} className="action-btn">
                            <FontAwesomeIcon icon={faLink} style={{ marginRight: '6px' }} /> Open in New Window
                          </button>
                          {/* <button onClick={copyToClipboard} className="action-btn">
                            <FaIcons.FaCopy style={{ marginRight: '6px' }} /> Copy Data
                          </button> */}
                          <button onClick={downloadResponse} className="action-btn">
                            <FontAwesomeIcon icon={faDownload} style={{ marginRight: '6px' }} /> Export to Excel
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="response-info">
                      <span>Status: <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px', color: '#27ae60' }} /> Data Loaded Successfully</span>
                      {selectedEndpoint && endpointDataCache[selectedEndpoint.endpoint] ? (
                        <span><FontAwesomeIcon icon={faSave} style={{ marginRight: '6px', color: '#27ae60' }} /> Cached Data - Generated: {new Date(endpointDataCache[selectedEndpoint.endpoint].timestamp).toLocaleString()}</span>
                      ) : (
                        <span>Generated: {new Date().toLocaleString()}</span>
                      )}
                      {Array.isArray(response) && (
                        <span>Total Records: {response.length}</span>
                      )}
                      {response && typeof response === 'object' && !Array.isArray(response) && (
                        <span>Data Fields: {Object.keys(response).length}</span>
                      )}
                    </div>
                    
                    <div className="response-content-container">
                      <div className="table-view">
                        {renderTableView(response)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <h2> Select a Report Type</h2>
              <p>Choose a report from the sidebar to view your data</p>
              
              <div className="explorer-tips">
                <h3><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '8px', color: '#ffc107' }} /> How to Use:</h3>
                <ul>
                  <li>Browse report categories to find the information you need</li>
                  <li>Fill in any required filters like dates or user types</li>
                  <li>Click "Load Data" to view your report in a table format</li>
                  <li>Export your data to Excel or CSV for offline use</li>
                  <li>Open reports in a new window for better viewing</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiExplorer;
